import React, { useEffect, useMemo, useState } from 'react';
import useLead from 'src/hooks/useLead';
import useStatus from 'src/hooks/useStatus';
import useSubstatus from 'src/hooks/useSubstatus';
import { useTranslation } from 'react-i18next';
import { Edit } from 'react-feather';
import { ISubstatus } from 'src/interfaces/substatus';
import { IStatus } from 'src/interfaces/status';
import { IStatuses } from './types';
import { filterSubstatus } from 'src/utils/status/filterSubstatus';
import {
    Card,
    CardContent,
    CardHeader,
    Divider,
    TextField,
    makeStyles,
    Box,
    Button,
    TextFieldProps,
} from '@material-ui/core';

const useStyles = makeStyles(() => ({
    root: {},
    submit: { height: 60 },
}));

export const Statuses = ({
    status,
    substatus,
    leadId,
    isDisabled = true,
    ...props
}: IStatuses) => {
    const classes = useStyles();
    const { t } = useTranslation();

    const { updateLead, loading } = useLead();
    const { statuses } = useStatus();
    const { substatuses } = useSubstatus();

    const [currentStatus, setCurrentStatus] = useState<string>('');
    const [currentSubstatus, setCurrentSubstatus] = useState<string>('');

    const substatusOptions = useMemo(
        () => filterSubstatus(currentStatus, substatuses),
        [currentStatus, substatuses]
    );

    useEffect(() => {
        if (!status || substatuses?.length <= 0) return;

        const nextSubstatusOptions = filterSubstatus(status, substatuses);
        setCurrentStatus(status);
        setCurrentSubstatus(
            substatus || nextSubstatusOptions[0]?._id || ''
        );
    }, [status, substatus, substatuses]);

    const handleSubmit = () => {
        if (isDisabled || loading || !currentStatus || !currentSubstatus)
            return;
        updateLead(
            {
                status: currentStatus,
                substatus: currentSubstatus,
            },
            leadId
        );
    };

    const handleChangeStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
        const nextStatus = e.target.value;
        const nextSubstatusOptions = filterSubstatus(nextStatus, substatuses);

        setCurrentStatus(nextStatus);
        setCurrentSubstatus(nextSubstatusOptions[0]?._id || '');
    };

    const INPUT_PROPS: TextFieldProps = {
        fullWidth: true,
        select: true,
        SelectProps: { native: true },
        variant: 'outlined',
    };

    return (
        <Card className={classes.root} {...props}>
            <CardHeader title={t('Leads.Status')} />
            <Divider />
            <CardContent>
                <Box display="flex" flexDirection="column" gridGap={15}>
                    <TextField
                        {...INPUT_PROPS}
                        name="status"
                        disabled={Boolean(isDisabled || loading)}
                        value={currentStatus}
                        onChange={handleChangeStatus}
                    >
                        {statuses.map((item: IStatus) => (
                            <option key={item._id} value={item._id}>
                                {t(`Status.${item.name}`)}
                            </option>
                        ))}
                    </TextField>
                    <TextField
                        {...INPUT_PROPS}
                        name="substatus"
                        disabled={Boolean(isDisabled || loading)}
                        value={currentSubstatus}
                        onChange={(e) => setCurrentSubstatus(e.target.value)}
                    >
                        {substatusOptions.map((item: ISubstatus) => (
                            <option key={item._id} value={item._id}>
                                {t(`Substatus.${item.name}`)}
                            </option>
                        ))}
                    </TextField>

                    {!isDisabled && (
                        <Button
                            className={classes.submit}
                            disabled={loading}
                            startIcon={<Edit />}
                            onClick={handleSubmit}
                            variant="contained"
                            color="primary"
                            size="large"
                            fullWidth
                        >
                            {t('Buttons.EditLead')}
                        </Button>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};
